/**
 * GravityZone2D Component
 *
 * Defines a region that modifies gravity for physics bodies within it.
 * Supports override, additive, point (planetary), and scale modes.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";

/**
 * Gravity zone type:
 * - 0: Override - Replace global gravity with zone's gravity vector
 * - 1: Additive - Add zone's gravity to current gravity
 * - 2: Point - Attract toward zone center (planetary gravity)
 * - 3: Scale - Multiply current gravity by scale factor
 */
export const enum GravityZoneType {
  Override = 0,
  Additive = 1,
  Point = 2,
  Scale = 3,
}

/**
 * Gravity zone shape:
 * - 0: Circle - Circular zone with inner/outer radius
 * - 1: Box - Rectangular zone (great for tunnels, corridors)
 * - 2: Capsule - Stadium shape (rounded rectangle)
 * - 3: Polygon - Arbitrary convex polygon
 */
export const enum GravityZoneShape {
  Circle = 0,
  Box = 1,
  Capsule = 2,
  Polygon = 3,
}

/**
 * Falloff curve for zone strength:
 * - 0: None - Full strength everywhere
 * - 1: Linear - Linear decrease from inner to outer radius
 * - 2: InverseSquare - Realistic falloff for point gravity
 * - 3: Smoothstep - Smooth hermite interpolation
 */
export const enum GravityFalloff {
  None = 0,
  Linear = 1,
  InverseSquare = 2,
  Smoothstep = 3,
}

@component("core.GravityZone2D")
@customEditor("GravityZone2DEditor")
@displayName("Gravity Zone 2D")
@icon("Globe")
@color("#9b59b6")
@category("Physics 2D")
@allowMultiple(false)
@requireComponent(["core.Transform"])
export class GravityZone2D {
  /** Zone shape (0=Circle, 1=Box, 2=Capsule, 3=Polygon) */
  @field({ type: "i32", label: "Shape" })
  shape: number = GravityZoneShape.Circle;

  /** Zone type (0=Override, 1=Additive, 2=Point, 3=Scale) */
  @field({ type: "i32", label: "Type" })
  zoneType: number = GravityZoneType.Override;

  /** Gravity vector for Override/Additive modes */
  @field({ type: "vec2", label: "Gravity" })
  gravity: { x: number; y: number } = { x: 0, y: 0 };

  /** Strength for Point mode, or scale factor for Scale mode */
  @field({ type: "f32", label: "Strength", step: 0.1 })
  strength: number = 9.81;

  /** Falloff curve (0=None, 1=Linear, 2=InverseSquare, 3=Smoothstep) */
  @field({ type: "i32", label: "Falloff" })
  falloff: number = GravityFalloff.Linear;

  // ─── Circle Shape ──────────────────────────────────────────────────────────

  /** Inner radius - full strength within this distance (Circle/Capsule) */
  @field({ type: "f32", label: "Inner Radius", min: 0, step: 0.1 })
  innerRadius: number = 0;

  /** Outer radius - zero strength beyond this distance (Circle/Capsule) */
  @field({ type: "f32", label: "Outer Radius", min: 0.1, step: 0.1 })
  outerRadius: number = 5;

  // ─── Box / Capsule Shape ───────────────────────────────────────────────────

  /** Size for Box shape, or capsule dimensions (width, height) */
  @field({ type: "vec2", label: "Size" })
  size: { x: number; y: number } = { x: 4, y: 2 };

  /** Direction for Capsule (0=Vertical, 1=Horizontal) */
  @field({ type: "i32", label: "Direction" })
  direction: number = 0;

  // ─── Polygon Shape ─────────────────────────────────────────────────────────

  /** Polygon points in local space */
  @field({ type: "array", elementType: "vec2", label: "Points" })
  points: { x: number; y: number }[] = [
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
  ];

  // ─── Common ────────────────────────────────────────────────────────────────

  /** Priority for blending (higher = more influence when zones overlap) */
  @field({ type: "i32", label: "Priority", min: 0, max: 100 })
  priority: number = 0;

  /** Layer mask - which physics layers are affected (default: all) */
  @field({ type: "i32", label: "Affected Layers", hidden: true })
  affectedLayers: number = 0xFFFF;
}
